import Papa from "papaparse";
import * as XLSX from "xlsx";

export interface FileValidationParams {
  alwaysRequired: string[];
  optionalFields?: string[];
}

function headerMatches(cell: string, header: string): boolean {
  if (cell === header) return true;
  if (cell.startsWith(header)) {
    const suffix = cell.slice(header.length);
    return /^[,.!@#$%^&*]+$/.test(suffix);
  }
  return false;
}

function buildRowChecker(
  alwaysRequired: string[],
  optionalFields: string[],
): (row: string[]) => boolean {
  return function rowHasAllHeaders(row: string[]): boolean {
    const normalizedRow = row
      .filter((cell) => cell != null)
      .map((cell) => String(cell).replace(/\r?\n/g, "").trim().toLowerCase())
      .filter((cell) => cell !== "");

    const allRequiredFound = alwaysRequired.every((req) => {
      const header = req.trim().toLowerCase();
      if (!header) return true;
      return normalizedRow.some((cell) => headerMatches(cell, header));
    });

    if (!allRequiredFound) return false;
    return optionalFields.every((opt) => {
      const trimmed = opt.trim().toLowerCase();
      if (!trimmed) return true;
      return normalizedRow.some((cell) => headerMatches(cell, trimmed));
    });
  };
}

/**
 * Reads the entire file (csv or excel) validates that a header row containing
 * all required/optional fields exists, and returns every row as a string[][].
 * returned array includes the header row so it can be made into CSV
 */
export async function validateAndReadFile(
  file: File,
  extension: string,
  params: FileValidationParams,
): Promise<string[][]> {
  const alwaysRequired = params.alwaysRequired;
  const optionalFields = params.optionalFields || [];
  const rowHasAllHeaders = buildRowChecker(alwaysRequired, optionalFields);

  const fieldsNeeded = [...alwaysRequired, ...optionalFields]
    .filter((f) => f.trim() !== "")
    .join(", ");

  if (extension === "csv") {
    return new Promise<string[][]>((resolve, reject) => {
      const allRows: string[][] = [];
      let found = false;
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        step: ({ data }) => {
          const rowArray = (data as string[]).map((c) =>
            String(c ?? "").trim(),
          );
          allRows.push(rowArray);
          if (!found && rowHasAllHeaders(rowArray)) {
            found = true;
          }
        },
        complete: () => {
          if (!found) {
            reject(
              new Error(
                `Could not find a row with required fields in the CSV.\nFields needed: ${fieldsNeeded}`,
              ),
            );
          } else {
            resolve(allRows);
          }
        },
        error: (err) => reject(err),
      });
    });
  } else {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];

    const allRows = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: "",
      raw: false,
      blankrows: false,
    }) as string[][];

    const found = allRows.some((r) =>
      rowHasAllHeaders(r.map((c) => (c != null ? String(c).trim() : ""))),
    );

    if (!found) {
      throw new Error(
        `Could not find a row with required fields in the Excel file.\nFields needed: ${fieldsNeeded}`,
      );
    }

    return allRows.map((r) => r.map((c) => (c != null ? String(c) : "")));
  }
}

export async function validateFileHeaders(
  file: File,
  extension: string,
  params: FileValidationParams,
  maxRows: number = 100,
): Promise<void> {
  const alwaysRequired = params.alwaysRequired;
  const optionalFields = params.optionalFields || [];
  const rowHasAllHeaders = buildRowChecker(alwaysRequired, optionalFields);

  const fieldsNeeded = [...alwaysRequired, ...optionalFields]
    .filter((f) => f.trim() !== "")
    .join(", ");

  if (extension === "csv") {
    await new Promise<void>((resolve, reject) => {
      let rowCount = 0;
      let found = false;
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        step: ({ data }, parser) => {
          rowCount++;
          if (rowCount > maxRows) {
            parser.abort();
            return;
          }
          const rowArray = (data as string[]).map((c) => c.trim());
          if (rowHasAllHeaders(rowArray)) {
            found = true;
            parser.abort();
          }
        },
        complete: () => {
          if (!found) {
            reject(
              new Error(
                `Could not find a row with required fields within the first ${maxRows} CSV rows.\nFields needed: ${fieldsNeeded}`,
              ),
            );
          } else {
            resolve();
          }
        },
        error: (err) => reject(err),
      });
    });
  } else {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, {
      type: "array",
      sheetRows: 0,
    });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];

    // Extend the range to catch header rows beyond !ref in malformed files
    const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
    const extendedRange = {
      s: { r: 0, c: 0 },
      e: { r: Math.max(range.e.r, maxRows - 1), c: range.e.c },
    };
    sheet["!ref"] = XLSX.utils.encode_range(extendedRange);

    const allRows = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: "",
      raw: false,
      blankrows: true,
    }) as string[][];

    const rows = allRows.slice(0, maxRows);
    const found = rows.some((r) =>
      rowHasAllHeaders(r.map((c) => (c != null ? String(c).trim() : ""))),
    );

    if (!found) {
      throw new Error(
        `Could not find a row with required fields within the first ${maxRows} Excel rows.\nFields needed: ${fieldsNeeded}`,
      );
    }
  }
}
